import * as React from "react";
import { StyledModal } from "./StyledModal";
import { DataService } from "../../services/DataService";
import { Button, Form, Spinner, Stack } from "react-bootstrap";
import { Code, IsUserSinfra } from "../../lib/utils";
import { faInfoCircle, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useCurrentDepartmentHook } from "../SupplierBar/SupplierBar";

export interface NullReportModalProps {
  onDismiss(): void;
  disabled: boolean;
  onSubmitted(msg: string): void;
}

export function NullReportModal(props: NullReportModalProps) {
  const { department, reportedPeriods, departmentLoaded } = useCurrentDepartmentHook();
  const { t } = useTranslation();

  // ---------- Helpers: period keys (LOCAL, aldrig toISOString pga UTC-buggar) ----------
  const toPeriodKeyLocal = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const normalizePeriodKey = (p: string) => (p ?? "").trim().slice(0, 7);

  const currentMonthKey = () => toPeriodKeyLocal(new Date());

  const nextMonthKey = () => {
    const now = new Date();
    return toPeriodKeyLocal(new Date(now.getFullYear(), now.getMonth() + 1, 1));
  };

  const isPastMonth = (p: string) => {
    const key = normalizePeriodKey(p);
    return key.length === 7 && key < currentMonthKey();
  };

  // ✅ NEW: allow current month too (past OR current)
  const isPastOrCurrentMonth = (p: string) => {
    const key = normalizePeriodKey(p);
    return key.length === 7 && key <= currentMonthKey();
  };

  const uniq = (arr: string[]) => Array.from(new Set(arr));

  // Department: redan rapporterat om kickback/statistik/noll finns
  const hasInvoicesNotReported = (entry: any) =>
  Array.isArray(entry?.invoicesNotReported) && entry.invoicesNotReported.length > 0;

const isAlreadyReportedDept = (p: string) => {
  const key = normalizePeriodKey(p);
  const entry = reportedPeriods?.[key];
  console.log(p,entry,'NULLERPORT ON SUBMITDEPARTMENTVALIDATION');
  console.log(!entry,'!entry')
  if (!entry) return false;

  // 1) Om det finns underlag kvar => då är perioden INTE rapporterad klart
  // (och man ska inte nollrapportera)
  console.log(hasInvoicesNotReported(entry),'hasInvoicesNotReported(entry)')
  if (hasInvoicesNotReported(entry)) return false;

  // 2) Kickback finns => rapporterat => blocka nullreport
  console.log(entry.kickbackReport === true,'entry.kickbackReport === true');
  if (entry.kickbackReport === true && entry.statisticReport == false) return true;

  // 3) Om båda false => tolka som nullreport/“klart finns” => blocka
  console.log(entry.kickbackReport === false && entry.statisticReport === false,'entry.kickbackReport === false && entry.statisticReport === false')
  if (entry.kickbackReport === false && entry.statisticReport === false) return true;

  // 4) Annars: vi har inget säkert bevis på att statistikrapport finns i overview-output
  // så vi tillåter nullreport (förutsatt att invoicesNotReported var tomt)
  return false;
};


  // ---------- State ----------
  const [period, setPeriod] = React.useState<string>(currentMonthKey());
  const [isSaving, setSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [missingReports, setMissingReports] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isPeriodLoading, setIsPeriodLoading] = React.useState(true);
  const [isPeriodReported, setIsPeriodReported] = React.useState(false);

  // Guards mot race conditions
  const checkIdRef = React.useRef(0);
  const fetchIdRef = React.useRef(0);

  // ---------- Build list (missade + current om den finns i base + ev nästa månad) ----------
  const buildPeriodsList = React.useCallback(
    async (basePeriods: string[]) => {
      const candidates = (basePeriods ?? [])
        .map(normalizePeriodKey)
        .filter(isPastOrCurrentMonth);

      const checkedBase = await Promise.all(
        uniq(candidates).map(async (candidate) => {
          const canNull = department
            ? await DataService.NullReports.CanNullreportOnSelectedPeriod(
                candidate,
                department.id,
              )
            : await DataService.NullReports.CanNullreportOnSelectedPeriod(
                candidate,
              );

          return canNull ? candidate : null;
        }),
      );

      const base = checkedBase.filter((p): p is string => p !== null);
      const nm = nextMonthKey();

      let addNext = false;
      if (department) {
        addNext = await DataService.NullReports.CanNullreportOnSelectedPeriod(
          nm,
          department.id,
        );
      } else {
        addNext = await DataService.NullReports.CanNullreportOnSelectedPeriod(
          nm,
        );
      }

      return uniq(addNext ? [...base, nm] : base).map(normalizePeriodKey)
    .filter(isPastOrCurrentMonth)
    .sort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [department, reportedPeriods]
  );

  // ---------- Fetch data (department vs non-department) ----------
  React.useEffect(() => {
    const fetchData = async () => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);
      setErrorMessage("");

      try {
        if (!departmentLoaded) return;

        if (IsUserSinfra()) {
          if (fetchId !== fetchIdRef.current) return;
          setMissingReports([]);
          setPeriod(currentMonthKey());
          return;
        }

        if (department) {
          // NOTE: behåller din befintliga logik för base här (statisticReport===true)
          // men nu kommer current month kunna vara med om den finns i base.
          const base = Object.entries(reportedPeriods ?? {})
            .filter(([_, v]) => v?.statisticReport === true && v?.kickbackReport === true)
            .map(([k]) => k);

          const list = await buildPeriodsList(base);
          if (fetchId !== fetchIdRef.current) return;

          setMissingReports(list);

          // Välj en rimlig default:
          // - om current finns → välj current
          // - annars om next finns → välj next
          // - annars välj sista i listan (senaste)
          const cm = currentMonthKey();
          const nm = nextMonthKey();
          const next =
            list.includes(cm) ? cm :
            list.includes(nm) ? nm :
            (list.length ? list[list.length - 1] : nm);

          setPeriod(prev => (normalizePeriodKey(prev) === next ? prev : next));
        } else {
          const base = await DataService.Invoices.GetMissedPeriods();
          console.log(base,'HÄR ÄR BASE')
          const list = await buildPeriodsList(base ?? []);
          if (fetchId !== fetchIdRef.current) return;

          setMissingReports(list);

          const cm = currentMonthKey();
          const nm = nextMonthKey();
          const next =
            list.includes(cm) ? cm :
            list.includes(nm) ? nm :
            (list.length ? list[list.length - 1] : nm);

          setPeriod(prev => (normalizePeriodKey(prev) === next ? prev : next));
        }
      } catch (e) {
        console.error("NullReportModal fetchData error:", e);
        if (fetchId !== fetchIdRef.current) return;

        setMissingReports([]);
        const nm = nextMonthKey();
        setPeriod(prev => (normalizePeriodKey(prev) === nm ? prev : nm));
      } finally {
        if (fetchId === fetchIdRef.current) setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentLoaded, department, reportedPeriods, buildPeriodsList]);

  // ---------- Reset (undvik att gammalt alreadyReported blinkar vid periodbyte) ----------
  React.useEffect(() => {
    setIsPeriodReported(false);
    setErrorMessage("");
  }, [period]);

  // ---------- Period reported check (disable button + show info) ----------
  React.useEffect(() => {
    const check = async () => {
      const checkId = ++checkIdRef.current;
      setIsPeriodLoading(true);

      try {
        const key = normalizePeriodKey(period);
        if (!key || key.length !== 7) {
          if (checkId === checkIdRef.current) setIsPeriodReported(false);
          return;
        }

      if (department) {
  // För dept: använd samma safetycheck som non-dept, fast med departmentId.
  const canNull = await DataService.NullReports.CanNullreportOnSelectedPeriod(
    key,
    department.id
  );

  if (checkId === checkIdRef.current)
    setIsPeriodReported(!canNull); // true = blocka
}
 else {
          // endpointen returnerar "canNullReport"
          const canNull = await DataService.NullReports.CanNullreportOnSelectedPeriod(key);
          if (checkId === checkIdRef.current) setIsPeriodReported(!canNull);
        }
      } catch (e) {
        console.error("NullReportModal checkPeriodReported error:", e);
        if (checkId === checkIdRef.current) setIsPeriodReported(false);
      } finally {
        if (checkId === checkIdRef.current) setIsPeriodLoading(false);
      }
    };

    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, department, reportedPeriods]);

  // ---------- UI helpers ----------
  function opt(p: string) {
    const key = normalizePeriodKey(p);
    const [y, m] = key.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, 1);

    const text = `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`;
    return (
      <option key={key} style={{ textTransform: "capitalize" }} value={key}>
        {text}
      </option>
    );
  }

  function generateMonthOptions() {
    if (missingReports.length > 0) {
      return missingReports.map((p) => opt(p));
    }
    
    const cm = currentMonthKey();
    //const nm = nextMonthKey();
    return uniq([cm/*, nm*/]).map((p) => opt(p));
  }
  






  const onDismiss = () => {
    if (isSaving) return;
    props.onDismiss();
  };

  const onClickSubmit = async () => {
    setErrorMessage("");
    setSaving(true);

    try {
      const key = normalizePeriodKey(period);

      // ✅ Nu tillåter vi: past + current + next
      const allowed =
        isPastMonth(key) ||
        key === currentMonthKey() /*||
        key === nextMonthKey();*/

      if (!allowed) {
        setErrorMessage("Ogiltig period.");
        return;
      }

      // Dept-affärsregel lokalt
     
      // if (department && isAlreadyReportedDept(key)) {
      //   setErrorMessage(t("nullReport.alreadyReported").toString());
      //   return;
      // }
      if (department) {
  const canNull = await DataService.NullReports.CanNullreportOnSelectedPeriod(key, department.id);
  if (!canNull) {
    setErrorMessage(t("nullReport.alreadyReported").toString());
    return;
  }
}


      // Non-dept: låt backend vara source-of-truth som safetycheck
      if (!department) {
        const canNull = await DataService.NullReports.CanNullreportOnSelectedPeriod(key);
        if (!canNull) {
          setErrorMessage(t("nullReport.alreadyReported").toString());
          return;
        }
      }

      const result = await DataService.NullReports.Post({
        period: key,
        departmentId: department?.id,
      });

      if (result.Code === Code.OK) {
        props.onSubmitted(result.Message);
        return;
      }

      setErrorMessage(result.Message);
    } catch (e) {
      console.error("NullReportModal submit error:", e);
      setErrorMessage("Något gick fel.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledModal
      headerStyle={{ backgroundColor:'#173c53',color:'white' }}
      onDismiss={onDismiss}
      title={t("nullReport.nullReport").toString()}
      body={
        <Stack className="submit-modal-body" gap={3}>
          <Form.Group>
            <Form.Label>{t("general.period")}:</Form.Label>

            {loading ? (
              <div>
                <Spinner />
              </div>
            ) : (
              <Form.Select
                style={{ textTransform: "capitalize" ,backgroundColor:'#f6f2ed'}}
                value={normalizePeriodKey(period)}
                onChange={(ev) => {
    const next = normalizePeriodKey(ev.target.value);
    setPeriod(next);
  }}
              >
                {generateMonthOptions()}
              </Form.Select>
            )}
          </Form.Group>

          {!loading && !isPeriodLoading && errorMessage && (
            <div className="error-message">
              <span>{t("general.errorMsg")}:</span> {errorMessage}
            </div>
          )}

          {!loading && !isPeriodLoading && isPeriodReported && !IsUserSinfra() && (
            <div className="info-message">
              <FontAwesomeIcon icon={faInfoCircle} />{" "}
              {t("nullReport.alreadyReported")}
            </div>
          )}
        </Stack>
      }
      footer={
        <Stack direction="horizontal" gap={2}>
          <Button
            style={{ color: "rgb(23, 61, 84)" }}
            disabled={
              props.disabled ||
              isSaving ||
              IsUserSinfra() ||
              loading ||
              isPeriodLoading ||
              isPeriodReported
            }
            className="button button-submit"
            onClick={onClickSubmit}
          >
            {isSaving ? (
              <Spinner animation="border" size={"sm"} />
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4 }} />
                {t("general.send")}
              </>
            )}
          </Button>

          <Button
            disabled={isSaving}
            className="button button-cancel"
            onClick={onDismiss}
          >
            {t("general.close")}
          </Button>
        </Stack>
      }
    />
  );
}
